/**
 * Game Store - Zustand
 * Manages FogView, match status, timers, check state, last move
 * Server-authoritative: all state derived from server events only
 */

import { create } from 'zustand';
import type { FogView, VisibleMove, CaptureEvent, EndgameResult } from '../types/game';
import type { MatchStatus, Color } from '../types/common';

/**
 * Opponent connection state for reconnect grace period banner
 */
export interface OpponentConnectionState {
  isDisconnected: boolean;
  player: Color | null;
  graceUntil: number | null; // Unix timestamp (seconds) when grace period expires
}

/**
 * Game store state
 */
export interface GameState {
  // Match state
  status: MatchStatus;
  viewerColor: Color | null;
  matchId: string | null;
  
  // Fog of War view (server-authoritative)
  fogView: FogView | null;
  
  // Timer (server-authoritative snapshot)
  timerSnapshot: number | null; // Unix timestamp of timer start
  timerDuration: number; // Duration in seconds (90 setup, 60 turn)
  timerPaused: boolean;
  
  // Game state
  inCheck: boolean;
  lastMove: VisibleMove | null;
  lastCapture: CaptureEvent | null;
  
  // Setup phase
  setupAccepted: boolean;
  isVsAi: boolean;
  opponentPieceSkinId: string | null;
  
  // Endgame
  endgameResult: EndgameResult | null;
  
  // Opponent connection state (for reconnect grace period banner)
  opponentConnection: OpponentConnectionState;
  
  // Move submission lock (non-optimistic UI)
  isSubmittingMove: boolean;
  
  // Actions
  setMatchStatus: (status: MatchStatus) => void;
  setViewerColor: (color: Color) => void;
  setMatchId: (id: string | null) => void;
  setFogView: (view: FogView | null) => void;
  setTimer: (snapshot: number, duration: number) => void;
  setTimerPaused: (paused: boolean) => void;
  setInCheck: (inCheck: boolean) => void;
  setLastMove: (move: VisibleMove | null) => void;
  setLastCapture: (capture: CaptureEvent | null) => void;
  setSetupAccepted: (accepted: boolean) => void;
  setIsVsAi: (isVsAi: boolean) => void;
  setOpponentPieceSkinId: (skinId: string | null) => void;
  setEndgameResult: (result: EndgameResult | null) => void;
  setOpponentConnection: (state: Partial<OpponentConnectionState>) => void;
  setSubmittingMove: (isSubmitting: boolean) => void;
  
  // Reset
  resetGame: () => void;
  resetMatch: () => void;
}

/**
 * Initial state
 */
const initialState: Omit<GameState, keyof { 
  setMatchStatus: any; setViewerColor: any; setMatchId: any; setFogView: any;
  setTimer: any; setTimerPaused: any; setInCheck: any; setLastMove: any;
  setLastCapture: any; setSetupAccepted: any; setIsVsAi: any; 
  setOpponentPieceSkinId: any; setEndgameResult: any; setOpponentConnection: any;
  setSubmittingMove: any; resetGame: any; resetMatch: any;
}> = {
  status: 'WAITING_PLAYERS',
  viewerColor: null,
  matchId: null,
  fogView: null,
  timerSnapshot: null,
  timerDuration: 90,
  timerPaused: false,
  inCheck: false,
  lastMove: null,
  lastCapture: null,
  setupAccepted: false,
  isVsAi: false,
  opponentPieceSkinId: null,
  endgameResult: null,
  opponentConnection: {
    isDisconnected: false,
    player: null,
    graceUntil: null,
  },
  isSubmittingMove: false,
};

/**
 * Game store
 */
export const useGameStore = create<GameState>((set) => ({
  ...initialState,
  
  setMatchStatus: (status: MatchStatus) => {
    set({ status });
    
    // Auto-adjust timer duration based on phase
    if (status === 'SETUP' || status === 'SETUP_LOCKED') {
      set({ timerDuration: 90 });
    } else if (status === 'IN_PROGRESS') {
      set({ timerDuration: 60 });
    }
  },
  
  setViewerColor: (viewerColor: Color) => set({ viewerColor }),
  
  setMatchId: (matchId: string | null) => set({ matchId }),
  
  setFogView: (fogView: FogView | null) => set({ fogView }),
  
  setTimer: (timerSnapshot: number, timerDuration: number) => 
    set({ timerSnapshot, timerDuration, timerPaused: false }),
  
  setTimerPaused: (timerPaused: boolean) => set({ timerPaused }),
  
  setInCheck: (inCheck: boolean) => set({ inCheck }),
  
  setLastMove: (lastMove: VisibleMove | null) => set({ lastMove }),
  
  setLastCapture: (lastCapture: CaptureEvent | null) => set({ lastCapture }),
  
  setSetupAccepted: (setupAccepted: boolean) => set({ setupAccepted }),
  
  setIsVsAi: (isVsAi: boolean) => set({ isVsAi }),
  
  setOpponentPieceSkinId: (opponentPieceSkinId: string | null) => set({ opponentPieceSkinId }),
  
  setEndgameResult: (endgameResult: EndgameResult | null) => set({ endgameResult }),
  
  setOpponentConnection: (state: Partial<OpponentConnectionState>) => 
    set((prev) => ({ opponentConnection: { ...prev.opponentConnection, ...state } })),
  
  setSubmittingMove: (isSubmittingMove: boolean) => set({ isSubmittingMove }),
  
  resetGame: () => set({
    ...initialState,
    matchId: useGameStore.getState().matchId,
    viewerColor: useGameStore.getState().viewerColor,
    isVsAi: useGameStore.getState().isVsAi,
    opponentPieceSkinId: useGameStore.getState().opponentPieceSkinId,
  }),
  
  resetMatch: () => set(initialState),
}));

/**
 * Selectors for common use cases
 */

// Get current turn from fogView
export const useCurrentTurn = () => 
  useGameStore((state) => state.fogView?.turn ?? null);

// Get own pieces
export const useOwnPieces = () => 
  useGameStore((state) => state.fogView?.own_pieces ?? []);

// Get opponent squares (fog)
export const useOpponentSquares = () => 
  useGameStore((state) => state.fogView?.opponent_squares ?? []);

// Get graveyard
export const useGraveyard = () => 
  useGameStore((state) => state.fogView?.graveyard ?? []);

// Check if in check
export const useInCheck = () => 
  useGameStore((state) => state.inCheck);

// Get timer remaining (calculated from server snapshot)
export const useTimerRemaining = () => {
  const { timerSnapshot, timerDuration, timerPaused, status } = useGameStore(
    (state) => ({ timerSnapshot: state.timerSnapshot, timerDuration: state.timerDuration, timerPaused: state.timerPaused, status: state.status })
  );
  
  if (!timerSnapshot || timerPaused || status === 'FINISHED' || status === 'WAITING_PLAYERS') {
    return timerDuration;
  }
  
  const elapsed = Math.floor((Date.now() / 1000) - timerSnapshot);
  const remaining = Math.max(0, timerDuration - elapsed);
  return remaining;
};

// Check if it's viewer's turn
export const useIsMyTurn = () => 
  useGameStore((state) => 
    state.fogView && state.viewerColor 
      ? state.fogView.turn === state.viewerColor 
      : false
  );

// Check if game is in setup phase
export const useIsSetupPhase = () => 
  useGameStore((state) => state.status === 'SETUP' || state.status === 'SETUP_LOCKED');

// Check if game is in progress
export const useIsInProgress = () => 
  useGameStore((state) => state.status === 'IN_PROGRESS');

// Check if game is finished
export const useIsFinished = () => 
  useGameStore((state) => state.status === 'FINISHED');

// Get endgame result
export const useEndgameResult = () => 
  useGameStore((state) => state.endgameResult);

// Get last move for arrow rendering
export const useLastMove = () => 
  useGameStore((state) => state.lastMove);

// Check if vs AI
export const useIsVsAi = () => 
  useGameStore((state) => state.isVsAi);

// Get opponent connection state (for reconnect banner)
export const useOpponentConnection = () => 
  useGameStore((state) => state.opponentConnection);

// Check if opponent is disconnected with grace period
export const useOpponentDisconnected = () => 
  useGameStore((state) => state.opponentConnection.isDisconnected);

// Get move submission lock state
export const useIsSubmittingMove = () => 
  useGameStore((state) => state.isSubmittingMove);

// Combined game state selector
export const useGame = () => {
  const status = useGameStore((state) => state.status);
  const viewerColor = useGameStore((state) => state.viewerColor);
  const matchId = useGameStore((state) => state.matchId);
  const fogView = useGameStore((state) => state.fogView);
  const timerSnapshot = useGameStore((state) => state.timerSnapshot);
  const timerDuration = useGameStore((state) => state.timerDuration);
  const timerPaused = useGameStore((state) => state.timerPaused);
  const inCheck = useGameStore((state) => state.inCheck);
  const lastMove = useGameStore((state) => state.lastMove);
  const lastCapture = useGameStore((state) => state.lastCapture);
  const setupAccepted = useGameStore((state) => state.setupAccepted);
  const isVsAi = useGameStore((state) => state.isVsAi);
  const opponentPieceSkinId = useGameStore((state) => state.opponentPieceSkinId);
  const endgameResult = useGameStore((state) => state.endgameResult);
  const opponentConnection = useGameStore((state) => state.opponentConnection);
  const isSubmittingMove = useGameStore((state) => state.isSubmittingMove);
  
  const setMatchStatus = useGameStore((state) => state.setMatchStatus);
  const setViewerColor = useGameStore((state) => state.setViewerColor);
  const setMatchId = useGameStore((state) => state.setMatchId);
  const setFogView = useGameStore((state) => state.setFogView);
  const setTimer = useGameStore((state) => state.setTimer);
  const setTimerPaused = useGameStore((state) => state.setTimerPaused);
  const setInCheck = useGameStore((state) => state.setInCheck);
  const setLastMove = useGameStore((state) => state.setLastMove);
  const setLastCapture = useGameStore((state) => state.setLastCapture);
  const setSetupAccepted = useGameStore((state) => state.setSetupAccepted);
  const setIsVsAi = useGameStore((state) => state.setIsVsAi);
  const setOpponentPieceSkinId = useGameStore((state) => state.setOpponentPieceSkinId);
  const setEndgameResult = useGameStore((state) => state.setEndgameResult);
  const setOpponentConnection = useGameStore((state) => state.setOpponentConnection);
  const setSubmittingMove = useGameStore((state) => state.setSubmittingMove);
  const resetGame = useGameStore((state) => state.resetGame);
  const resetMatch = useGameStore((state) => state.resetMatch);
  
  return {
    status,
    viewerColor,
    matchId,
    fogView,
    timerSnapshot,
    timerDuration,
    timerPaused,
    inCheck,
    lastMove,
    lastCapture,
    setupAccepted,
    isVsAi,
    opponentPieceSkinId,
    endgameResult,
    opponentConnection,
    isSubmittingMove,
    setMatchStatus,
    setViewerColor,
    setMatchId,
    setFogView,
    setTimer,
    setTimerPaused,
    setInCheck,
    setLastMove,
    setLastCapture,
    setSetupAccepted,
    setIsVsAi,
    setOpponentPieceSkinId,
    setEndgameResult,
    setOpponentConnection,
    setSubmittingMove,
    resetGame,
    resetMatch,
  };
};